import { useNavigate } from "react-router-dom";
import { intl } from "LocaleWrapper";
import { useMutation, useQuery } from "@apollo/client";
import { QuickFilterTypes, Table } from "@cofano/cck";
import DeleteIcon from "@mui/icons-material/Delete";
import { TableId, useFrontendTable } from "components/table";
import { useConfirm } from "components/hooks/useConfirm";
import { GetListTemplatesDocument, GetListTemplatesQuery } from "__generated__/view/templates/graphql/template.queries.generated";
import { DeleteTemplateDocument } from "__generated__/view/templates/graphql/template.mutations.generated";
import { ChildPropsFromArray } from "types/commons";

type TemplateFromQuery = ChildPropsFromArray<GetListTemplatesQuery, "listTemplates">;

const TemplateDefaultTable = () => {

	const confirm = useConfirm();
	const navigate = useNavigate();

	const { data, refetch, loading } = useQuery(GetListTemplatesDocument);

	const { tableProps, refetchAfterDelete } = useFrontendTable({
		id: TableId.TemplateDefault,
		content: (data?.listTemplates ?? []).map(it => ({ ...it, id: it.id ?? undefined })),
	});

	const [ deleteDefaultTemplate ] = useMutation(DeleteTemplateDocument);

	const deleteTemplate = (id: number) => {
		deleteDefaultTemplate({
			variables: {
				id: id,
			},
		}).then(() => {
			refetchAfterDelete();
			refetch();
		});
	};

	return (
		<Table
			{...tableProps}
			onGetRowId={(row: TemplateFromQuery) => row.name}
			contentLoading={loading}
			onRowDoubleClick={row => {
				const template = row as TemplateFromQuery;

				if (template.id) {
					navigate(`/view/templates/${template.id}/edit`);
				} else {
					navigate(`/view/templates/${template.name}/edit`);
				}
			}}
			columns={[ {
				id: "name",
				title: intl.formatMessage({ id: "TEMPLATES_NAME" }),
				sortable: true,
				filterComponent: QuickFilterTypes.TEXT,
			}, {
				id: "preferredLanguage.description",
				title: intl.formatMessage({ id: "TEMPLATES_LANGUAGE" }),
				render: (row: TemplateFromQuery) => row.preferredLanguage?.description ?? intl.formatMessage({ id: "N/A" }),
			} ]}
			rowActions={[ {
				icon: <DeleteIcon />,
				disabled: row => !row.id,
				onClick: (row) => {
					confirm({
						type: "DELETE",
					}).then(() => {
						deleteTemplate(row.id);
					});
				},
			} ]} />
	);
};

export default TemplateDefaultTable;
